import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import ExpandableDataTable from '@/components/UI/ExpandableDataTable';
import Button from '@/components/UI/Button';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import { showToast } from '@/components/Layout/Toast';
import type { ColumnDef, Row } from '@/components/UI/dataTableConfig';
import type { EntryScorecardsRow, ResourceAccessListItem, TradingStrategy } from '@einhaus/project314-types';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner.tsx';
import { trpc, trpcUnbatched } from '@/api/trpc.ts';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useStrategyReadinessSummaries } from '@/hooks/useStrategyReadinessSummaries';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { formatPercentage } from '@/utils/formatters';
import ResourceAccessBadge from '@/components/resourceAccess/ResourceAccessBadge';
import ShareResourceModal from '@/components/resourceAccess/ShareResourceModal';
import Modal from '@/components/UI/Modal';
import FormInput from '@/components/UI/FormInput';
import FormSelect from '@/components/UI/FormSelect';
import SearchSelect, { type SearchSelectItem } from '@/components/UI/SearchSelect';
import { FaColumns, FaTrophy } from 'react-icons/fa';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@/types/appRouter';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type FamilyVariantMeta = RouterOutputs['strategyVariant']['listFamilies']['families'][number]['variants'][number];
type CoverageSummary = RouterOutputs['backtest']['getStrategiesCoverageSummary']['summaries'][number];

const formatCoverageLabel = (summary: { entitiesWithBacktestsCount: number; totalEntitiesInUniverse: number } | undefined): string => {
    if (!summary) return 'View backtests';
    if (summary.totalEntitiesInUniverse === 0) return 'No universe';
    if (summary.entitiesWithBacktestsCount === 0) return 'No backtests';

    return `${summary.entitiesWithBacktestsCount}/${summary.totalEntitiesInUniverse} backtested`;
};

// Avg % profit per entered trade is stored as a percentage value (e.g. 6.06 => +6.06%).
const formatTradeProfitSignal = (value: number | null | undefined): string | null =>
    value === null || value === undefined || !Number.isFinite(value) ? null : `${value > 0 ? '+' : ''}${formatPercentage(value, 1)}/trade`;

const formatWinRateSignal = (value: number | null | undefined): string | null =>
    value === null || value === undefined || !Number.isFinite(value) ? null : `${Math.round(value)}% win`;

// Compact net P&L across entered trades, e.g. +$3.1M / -$118.2M / +$540.
const formatNetProfitSignal = (value: number | null | undefined): string | null => {
    if (value === null || value === undefined || !Number.isFinite(value)) return null;

    const sign = value < 0 ? '-' : '+';
    const magnitude = Math.abs(value);

    if (magnitude >= 1_000_000) return `${sign}$${(magnitude / 1_000_000).toFixed(1)}M`;
    if (magnitude >= 1_000) return `${sign}$${(magnitude / 1_000).toFixed(1)}K`;

    return `${sign}$${Math.round(magnitude)}`;
};

const formatCompactCount = (value: number): string => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;

    return `${value}`;
};

const signalToneClass = (value: number | null | undefined): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return 'text-zinc-400';
    if (value > 0) return 'text-green-300';
    if (value < 0) return 'text-red-300';

    return 'text-zinc-300';
};

type ScorecardAttachmentMode = 'none' | 'attach';
type ScreenerArtifactMode = 'none' | 'reuseAttached' | 'clone' | 'latestRevision';
type AeroArtifactMode = 'none';
type VariantDialogState = {
    aeroArtifactMode: AeroArtifactMode;
    description: string;
    name: string;
    scorecardAttachmentMode: ScorecardAttachmentMode;
    screenerArtifactMode: ScreenerArtifactMode;
    scorecardId: number | null;
    sourceStrategy: TradingStrategy;
    variantLabel: string;
};

// Shared by the variations panel header and its rows so every cell lines up column-to-column. The panel spans the
// full (scrollable) table width, so it is capped to keep all five columns inside the viewport. The leading fixed
// column holds the id at a constant x position, so the ids read as a scannable column instead of drifting with name length.
const VARIATION_GRID = 'grid max-w-[1160px] grid-cols-[64px_minmax(0,1fr)_110px_minmax(0,230px)_auto] items-start gap-x-4';

function buildDefaultVariantName(strategyName: string): string {
    const suffix = ' Variant';
    const baseName = strategyName.length + suffix.length > 125 ? strategyName.slice(0, 125 - suffix.length) : strategyName;
    return `${baseName}${suffix}`;
}

function toScorecardAttachmentMode(value: string): ScorecardAttachmentMode {
    return value === 'attach' ? 'attach' : 'none';
}

function toScreenerArtifactMode(value: string): ScreenerArtifactMode {
    switch (value) {
        case 'reuseAttached':
        case 'clone':
        case 'latestRevision':
            return value;
        default:
            return 'none';
    }
}

function chooseDefaultScorecardId(scorecards: EntryScorecardsRow[], attachedScorecardId: number | null): number | null {
    return scorecards.find((scorecard) => scorecard.id === attachedScorecardId)?.id ?? scorecards[0]?.id ?? null;
}

// Plain-language list of what a variation adds on top of its source, for the create-dialog summary.
function describeVariantArtifacts(state: VariantDialogState): string[] {
    const adds: string[] = [];

    if (state.scorecardAttachmentMode === 'attach') {
        adds.push('an existing reusable scorecard (shared)');
    }

    if (state.screenerArtifactMode === 'reuseAttached') {
        adds.push('the same screener as the source (shared)');
    } else if (state.screenerArtifactMode === 'clone') {
        adds.push('a copy of the source screener');
    } else if (state.screenerArtifactMode === 'latestRevision') {
        adds.push('the latest version of the source screener');
    }

    return adds;
}

const VariantPlanSummary: React.FC<{ state: VariantDialogState }> = ({ state }) => {
    const adds = describeVariantArtifacts(state);

    return (
        <div className="rounded border border-zinc-700 bg-zinc-900/60 px-3 py-3 text-sm">
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-zinc-500">What this creates</div>
            <p className="text-zinc-200">
                An inactive variation of <span className="font-medium text-white">{state.sourceStrategy.name}</span>
                {adds.length === 0 ? ', with the same setup (no extra scorecard or screener).' : ', adding:'}
            </p>
            {adds.length > 0 && (
                <ul className="mt-1 list-disc space-y-0.5 pl-5 text-zinc-300">
                    {adds.map((add) => (
                        <li key={add}>{add}</li>
                    ))}
                </ul>
            )}
            <p className="mt-2 text-xs text-zinc-500">
                It starts paused and inherits the source strategy&apos;s global exit candidates by ID. No exit candidate is copied. Backtest
                it before going live.
            </p>
        </div>
    );
};

const TradingStrategies: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [error, setError] = useState<string | null>(null);
    const [sharingStrategy, setSharingStrategy] = useState<{ id: number; label: string } | null>(null);
    const [variantDialog, setVariantDialog] = useState<VariantDialogState | null>(null);

    const openCreateVariantDialog = useCallback((strategy: TradingStrategy) => {
        const defaultName = buildDefaultVariantName(strategy.name);

        setVariantDialog({
            aeroArtifactMode: 'none',
            description: strategy.description ?? '',
            name: defaultName,
            scorecardAttachmentMode: 'none',
            screenerArtifactMode: 'none',
            scorecardId: null,
            sourceStrategy: strategy,
            variantLabel: defaultName
        });
    }, []);

    const createVariantMutation = useTrpcMutation(trpc.strategyVariant.createFromStrategy.mutationOptions({}), {
        fallbackErrorMessage: 'Failed to create strategy variant',
        successMessage: 'Strategy variant created'
    });

    // Fetch strategies with tRPC
    const strategiesQuery = useAppQuery(
        {
            ...trpcUnbatched.strategy.fetchStrategies.queryOptions({
                requireActive: false,
                ids: []
            }),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load trading strategies',
            setError: (msg) => setError(msg)
        }
    );

    const accessQuery = useAppQuery(
        {
            ...trpc.resourceAccess.listAccessibleResources.queryOptions({
                resourceType: 'tradingStrategy'
            }),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load strategy access state',
            showErrorToast: true
        }
    );

    const strategyFamiliesQuery = useAppQuery(
        {
            ...trpc.strategyVariant.listFamilies.queryOptions(),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME
        },
        {
            fallbackErrorMessage: 'Failed to load strategy families',
            showErrorToast: true
        }
    );

    // Extract data from query
    const strategies = useMemo(() => strategiesQuery.data?.strategies || [], [strategiesQuery.data]);
    const strategyFamilies = useMemo(() => strategyFamiliesQuery.data?.families ?? [], [strategyFamiliesQuery.data]);
    const { isLoading } = strategiesQuery;

    const variantScorecardsQuery = useAppQuery(
        {
            ...trpc.scorecards.list.queryOptions(),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME,
            enabled: variantDialog !== null
        },
        {
            fallbackErrorMessage: 'Failed to load source scorecards',
            showErrorToast: true
        }
    );

    const sourceScorecards = useMemo(
        () =>
            (variantScorecardsQuery.data ?? []).filter(
                (scorecard) => scorecard.initializationStatus === 'ready' && scorecard.lockedAt !== null
            ),
        [variantScorecardsQuery.data]
    );

    const sourceScreenerRuleId = variantDialog?.sourceStrategy.activeScreenerRuleId ?? null;

    const sourceScorecardOptions = useMemo<SearchSelectItem[]>(
        () =>
            sourceScorecards.map((scorecard) => ({
                label: scorecard.name,
                meta: 'locked version',
                name: scorecard.name,
                value: scorecard.id
            })),
        [sourceScorecards]
    );

    const selectedVariantScorecardId =
        variantDialog?.scorecardAttachmentMode === 'attach'
            ? (variantDialog.scorecardId ??
              chooseDefaultScorecardId(sourceScorecards, variantDialog.sourceStrategy.activeScorecardId ?? null))
            : null;

    const isVariantMutationPending = createVariantMutation.isPending;

    const submitVariantDialog = useCallback(async () => {
        if (!variantDialog) {
            return;
        }

        const createResult = await createVariantMutation
            .mutateAsync({
                aero: {
                    action: variantDialog.aeroArtifactMode
                },
                sourceStrategyId: variantDialog.sourceStrategy.id,
                name: variantDialog.name,
                description: variantDialog.description,
                variantLabel: variantDialog.variantLabel,
                ...(variantDialog.scorecardAttachmentMode === 'attach' && selectedVariantScorecardId
                    ? { scorecardId: selectedVariantScorecardId }
                    : {}),
                ...(variantDialog.screenerArtifactMode !== 'none'
                    ? {
                          screener: {
                              action: variantDialog.screenerArtifactMode
                          }
                      }
                    : {})
            })
            .catch(() => null);

        if (!createResult) {
            return;
        }

        if (!createResult.success) {
            return;
        }

        if (createResult.scorecardAttachment && !createResult.scorecardAttachment.attached) {
            showToast(
                createResult.scorecardAttachment.warning ??
                    'The variant was created, but the scorecard could not be attached. Attach it from the strategy page.',
                'warning',
                true
            );
        }

        setVariantDialog(null);
        await queryClient.invalidateQueries(trpcUnbatched.strategy.fetchStrategies.pathFilter());
        await queryClient.invalidateQueries(trpc.strategyVariant.listFamilies.pathFilter());
        void navigate(`/user/tradingStrategy/${createResult.strategyId}`);
    }, [createVariantMutation, navigate, queryClient, selectedVariantScorecardId, variantDialog]);

    const accessByStrategyId = useMemo(() => {
        const map: Record<number, ResourceAccessListItem> = {};

        for (const item of accessQuery.data ?? []) {
            map[item.resource.resourcePk] = item;
        }

        return map;
    }, [accessQuery.data]);

    const ownedStrategyIds = useMemo(
        () => strategies.filter((strategy) => accessByStrategyId[strategy.id]?.ownedByUser === true).map((strategy) => strategy.id),
        [accessByStrategyId, strategies]
    );

    const coverageSummaryQuery = useAppQuery(
        {
            ...trpc.backtest.getStrategiesCoverageSummary.queryOptions({
                strategyIds: ownedStrategyIds
            }),
            staleTime: DEFAULT_REQUISITIES_STALE_TIME,
            enabled: ownedStrategyIds.length > 0 && !accessQuery.isLoading
        },
        {
            fallbackErrorMessage: 'Failed to load backtest coverage summary',
            showErrorToast: true
        }
    );

    const coverageByStrategyId = useMemo(() => {
        const map: Record<number, CoverageSummary> = {};

        if (coverageSummaryQuery.data?.summaries) {
            for (const summary of coverageSummaryQuery.data.summaries) {
                map[summary.strategyId] = summary;
            }
        }

        return map;
    }, [coverageSummaryQuery.data]);

    const readinessSummaryQuery = useStrategyReadinessSummaries(ownedStrategyIds, {
        enabled: !accessQuery.isLoading,
        fallbackErrorMessage: 'Failed to load readiness summary'
    });

    const readinessByStrategyId = useMemo(() => {
        const map: Record<
            number,
            {
                strategyId: number;
                overallStatus: 'ready' | 'not_ready' | 'needs_attention';
                checklistTotal: number;
                checklistCompleted: number;
            }
        > = {};

        if (readinessSummaryQuery.data?.summaries) {
            for (const summary of readinessSummaryQuery.data.summaries) {
                map[summary.strategyId] = summary;
            }
        }

        return map;
    }, [readinessSummaryQuery.data]);

    // Show/hide spinner based on loading state
    useEffect(() => {
        if (isLoading) {
            showSpinner();
        } else {
            hideSpinner();
        }

        // Hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, [isLoading]);

    // Group strategies by family: top-level rows are base/standalone strategies; variations nest under their base.
    const { topLevelStrategies, variationsByBaseId, metaByStrategyId, familyIdByBaseId, baseIdByVariationId, strategyById } =
        useMemo(() => {
            const metaByStrategyId = new Map<number, FamilyVariantMeta>();
            const baseStrategyIdByFamilyId = new Map<number, number>();
            const familyIdByBaseId = new Map<number, number>();
            const baseIdByVariationId = new Map<number, number>();
            const strategyById = new Map<number, TradingStrategy>();

            for (const summary of strategyFamilies) {
                // Tolerate an older backend response that omits `variants` (deploy skew); the list still renders flat.
                if (summary.family.rootStrategyId !== null) {
                    baseStrategyIdByFamilyId.set(summary.family.id, summary.family.rootStrategyId);
                    familyIdByBaseId.set(summary.family.rootStrategyId, summary.family.id);
                }

                for (const variant of summary.variants ?? []) {
                    metaByStrategyId.set(variant.strategyId, variant);
                }
            }

            const variationsByBaseId = new Map<number, TradingStrategy[]>();
            const topLevelStrategies: TradingStrategy[] = [];

            for (const strategy of strategies) {
                strategyById.set(strategy.id, strategy);

                const meta = metaByStrategyId.get(strategy.id);
                const baseId = meta ? (baseStrategyIdByFamilyId.get(meta.familyId) ?? null) : null;

                // A base, an orphan (root deleted), or a strategy outside any family stays top-level; real variations nest.
                if (!meta || baseId === null || baseId === strategy.id) {
                    topLevelStrategies.push(strategy);
                } else {
                    const list = variationsByBaseId.get(baseId) ?? [];
                    list.push(strategy);
                    variationsByBaseId.set(baseId, list);
                    baseIdByVariationId.set(strategy.id, baseId);
                }
            }

            // Newest variation first. Ids are monotonic, so id order is creation order, and unlike the variant
            // metadata's createdAt the id is always on the strategy row even when the metadata lookup misses.
            for (const variations of variationsByBaseId.values()) {
                variations.sort((left, right) => right.id - left.id);
            }

            return {
                topLevelStrategies,
                variationsByBaseId,
                metaByStrategyId,
                familyIdByBaseId,
                baseIdByVariationId,
                strategyById
            };
        }, [strategies, strategyFamilies]);

    // Searching flattens the hierarchy: every strategy competes as its own row, so a variation you searched for
    // is the row you get back instead of a collapsed parent that gives no hint about what matched.
    const [strategySearch, setStrategySearch] = useState('');
    const isSearching = strategySearch.trim().length > 0;
    const tableStrategies = isSearching ? strategies : topLevelStrategies;

    // A bare strategy id is the fastest way in, so offer a direct jump instead of making the user hunt the row.
    const exactIdMatch = useMemo(() => {
        const raw = strategySearch.trim();

        if (!/^\d+$/.test(raw)) {
            return null;
        }

        return strategyById.get(Number(raw)) ?? null;
    }, [strategySearch, strategyById]);

    const handleSearchSubmit = useCallback(() => {
        if (!exactIdMatch) {
            return;
        }

        void navigate(`/user/tradingStrategy/${exactIdMatch.id}`);
    }, [exactIdMatch, navigate]);

    // Define columns for the strategy table
    const columns: ColumnDef<TradingStrategy>[] = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                minSize: 70,
                size: 80,
                maxSize: 110,
                cell: ({ row }) => (
                    <button
                        className="font-mono text-xs text-blue-400 hover:text-blue-300 hover:underline"
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            void navigate(`/user/tradingStrategy/${row.original.id}`);
                        }}
                    >
                        #{row.original.id}
                    </button>
                )
            },
            {
                id: 'name',
                accessorFn: (row) => {
                    // While searching, each family member is its own row, so a base must not answer for its variations too.
                    if (isSearching) {
                        return `${row.id} ${row.name} ${row.description ?? ''} ${metaByStrategyId.get(row.id)?.variantLabel ?? ''}`;
                    }

                    const variationText = (variationsByBaseId.get(row.id) ?? [])
                        .map((variation) => `${variation.name} ${variation.id}`)
                        .join(' ');

                    return `${row.id} ${row.name} ${row.description ?? ''} ${variationText}`;
                },
                header: 'Strategy',
                minSize: 240,
                size: 380,
                maxSize: 560,
                cell: ({ row }) => {
                    const strategy = row.original;
                    const variationCount = variationsByBaseId.get(strategy.id)?.length ?? 0;
                    const baseId = baseIdByVariationId.get(strategy.id) ?? null;
                    // Only populated for variations, which are rows only while searching; the tree keeps them in the panel.
                    const baseStrategy = baseId === null ? null : (strategyById.get(baseId) ?? null);
                    return (
                        <div>
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    className="text-left font-medium text-blue-400 hover:text-blue-300 hover:underline"
                                    type="button"
                                    onClick={() => navigate(`/user/tradingStrategy/${strategy.id}`)}
                                >
                                    {strategy.name}
                                </button>
                                {variationCount > 0 && (
                                    <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[11px] text-zinc-400">
                                        {variationCount} variation{variationCount === 1 ? '' : 's'}
                                    </span>
                                )}
                            </div>
                            {baseStrategy && (
                                <div className="mt-1 truncate text-[11px] text-zinc-500">
                                    <Link
                                        className="hover:text-zinc-300 hover:underline"
                                        title={`Variation of ${baseStrategy.name}`}
                                        to={`/user/tradingStrategy/${baseStrategy.id}`}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        variation of #{baseStrategy.id} {baseStrategy.name}
                                    </Link>
                                </div>
                            )}
                            {strategy.description && (
                                <div className="mt-0.5 max-w-[420px] truncate text-xs text-zinc-500" title={strategy.description}>
                                    {strategy.description}
                                </div>
                            )}
                        </div>
                    );
                }
            },
            {
                accessorKey: 'type',
                header: 'Type',
                minSize: 80,
                size: 120,
                maxSize: 180
            },
            {
                accessorKey: 'active',
                header: 'Status',
                minSize: 80,
                size: 100,
                maxSize: 150,
                cell: ({ row }) => (
                    <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                            row.original.active
                                ? 'bg-green-900/50 text-green-300 border border-green-700'
                                : 'bg-zinc-700 text-zinc-400 border border-zinc-600'
                        }`}
                    >
                        {row.original.active ? 'Active' : 'Inactive'}
                    </span>
                )
            },
            {
                id: 'access',
                header: 'Access',
                minSize: 110,
                size: 130,
                maxSize: 170,
                cell: ({ row }) => {
                    const access = accessByStrategyId[row.original.id];

                    if (!access) {
                        return <span className="text-xs text-zinc-500">Unknown</span>;
                    }

                    return <ResourceAccessBadge access={access} />;
                }
            },
            {
                id: 'backtestCoverage',
                header: 'Backtests',
                minSize: 120,
                size: 160,
                maxSize: 200,
                cell: ({ row }) => {
                    const strategy = row.original;
                    const access = accessByStrategyId[strategy.id];

                    if (!access?.ownedByUser) {
                        return <span className="text-xs text-zinc-500">Owner only</span>;
                    }

                    const summary = coverageByStrategyId[strategy.id];

                    if (coverageSummaryQuery.isLoading && !summary) {
                        return <span className="text-xs text-zinc-500">Loading…</span>;
                    }

                    if (!summary) {
                        return (
                            <button
                                className="text-left text-xs text-zinc-400 hover:text-zinc-200 underline-offset-2 hover:underline"
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void navigate(`/user/tradingStrategy/${strategy.id}?section=backtests`);
                                }}
                            >
                                Unknown (open status)
                            </button>
                        );
                    }

                    const { totalEntitiesInUniverse, entitiesWithBacktestsCount } = summary;

                    if (!totalEntitiesInUniverse || entitiesWithBacktestsCount === 0) {
                        return (
                            <button
                                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium bg-red-900/40 text-red-300 border border-red-700 hover:bg-red-800/60"
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void navigate(`/user/tradingStrategy/${strategy.id}?section=backtests`);
                                }}
                            >
                                <span className="h-2 w-2 rounded-full bg-red-400" />
                                None
                            </button>
                        );
                    }

                    const isComplete = entitiesWithBacktestsCount === totalEntitiesInUniverse;
                    const label = isComplete ? 'Complete' : 'Partial';

                    const colorClasses = isComplete
                        ? 'bg-green-900/40 text-green-300 border border-green-700'
                        : 'bg-amber-900/40 text-amber-200 border border-amber-700';

                    const dotColor = isComplete ? 'bg-green-400' : 'bg-amber-400';

                    return (
                        <button
                            className="flex flex-col gap-0.5 text-left"
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                void navigate(`/user/tradingStrategy/${strategy.id}?section=backtests`);
                            }}
                        >
                            <span
                                className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${colorClasses} hover:bg-opacity-75`}
                            >
                                <span className={`h-2 w-2 rounded-full ${dotColor}`} />
                                {label}
                            </span>
                            <span className="text-[10px] text-zinc-400 font-mono">
                                {entitiesWithBacktestsCount}/{totalEntitiesInUniverse} entities
                            </span>
                        </button>
                    );
                }
            },
            {
                id: 'backtestResults',
                header: 'Best result',
                minSize: 160,
                size: 210,
                maxSize: 260,
                accessorFn: (row): number | undefined => {
                    const summary = coverageByStrategyId[row.id];

                    if (!summary || summary.tradeCount === 0 || summary.avgTradeProfitPercentage === null) {
                        return undefined;
                    }

                    return summary.avgTradeProfitPercentage;
                },
                sortFn: 'basic',
                sortUndefined: 'last',
                cell: ({ row }) => {
                    const strategy = row.original;
                    const access = accessByStrategyId[strategy.id];

                    if (!access?.ownedByUser) {
                        return <span className="text-xs text-zinc-500">—</span>;
                    }

                    const summary = coverageByStrategyId[strategy.id];
                    const profitLabel = formatTradeProfitSignal(summary?.avgTradeProfitPercentage);
                    const netLabel = formatNetProfitSignal(summary?.netProfit);
                    const winLabel = formatWinRateSignal(summary?.winRate);

                    if (!summary || summary.tradeCount === 0 || (!profitLabel && !netLabel && !winLabel)) {
                        return <span className="text-xs text-zinc-500">—</span>;
                    }

                    return (
                        <button
                            className="flex flex-col gap-0.5 text-left"
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                void navigate(`/user/tradingStrategy/${strategy.id}?section=backtests`);
                            }}
                        >
                            <span
                                className="text-[10px] text-zinc-500"
                                title="Best sufficiently covered option-strategy and exit-candidate combination for the strategy's current version, ranked by return on risk capital"
                            >
                                Best current-version candidate
                            </span>
                            <span className={`text-xs font-medium ${signalToneClass(summary.avgTradeProfitPercentage)}`}>
                                {profitLabel ?? '—'}
                            </span>
                            <span className="text-[10px] text-zinc-400 font-mono">
                                {netLabel && <span className={signalToneClass(summary.netProfit)}>{netLabel}</span>}
                                {netLabel ? ' · ' : ''}
                                {winLabel ? `${winLabel} · ` : ''}
                                {formatCompactCount(summary.tradeCount)} trades
                            </span>
                        </button>
                    );
                }
            },
            {
                id: 'readiness',
                header: 'Dashboard',
                minSize: 120,
                size: 140,
                maxSize: 180,
                cell: ({ row }) => {
                    const strategy = row.original;
                    const access = accessByStrategyId[strategy.id];

                    if (!access?.ownedByUser) {
                        return <span className="text-xs text-zinc-500">Owner only</span>;
                    }

                    const summary = readinessByStrategyId[strategy.id];

                    if (readinessSummaryQuery.isLoading && !summary) {
                        return <span className="text-xs text-zinc-500">Loading…</span>;
                    }

                    if (!summary) {
                        return (
                            <button
                                className="text-left text-xs text-zinc-400 hover:text-zinc-200 underline-offset-2 hover:underline"
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void navigate(`/user/strategyDashboard/${strategy.id}`);
                                }}
                            >
                                View
                            </button>
                        );
                    }

                    const getStatusConfig = (status: 'ready' | 'not_ready' | 'needs_attention') => {
                        switch (status) {
                            case 'ready':
                                return {
                                    label: 'Ready',
                                    classes: 'bg-green-900/40 text-green-300 border border-green-700',
                                    dot: 'bg-green-400'
                                };
                            case 'needs_attention':
                                return {
                                    label: 'Attention',
                                    classes: 'bg-amber-900/40 text-amber-200 border border-amber-700',
                                    dot: 'bg-amber-400'
                                };
                            case 'not_ready':
                                return {
                                    label: 'Not Ready',
                                    classes: 'bg-red-900/40 text-red-300 border border-red-700',
                                    dot: 'bg-red-400'
                                };
                        }
                    };

                    const config = getStatusConfig(summary.overallStatus);

                    return (
                        <button
                            className="flex flex-col gap-0.5 text-left"
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                void navigate(`/user/strategyDashboard/${strategy.id}`);
                            }}
                        >
                            <span
                                className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium ${config.classes} hover:opacity-80`}
                            >
                                <span className={`h-2 w-2 rounded-full ${config.dot}`} />
                                {config.label}
                            </span>
                            {summary.checklistTotal > 0 && (
                                <span className="text-[10px] text-zinc-400 font-mono">
                                    {summary.checklistCompleted}/{summary.checklistTotal} items
                                </span>
                            )}
                        </button>
                    );
                }
            },
            {
                id: 'actions',
                header: 'Actions',
                minSize: 190,
                size: 300,
                maxSize: 360,
                cell: ({ row }) => {
                    const strategy = row.original;
                    const access = accessByStrategyId[strategy.id];
                    const canEdit = access?.canEdit ?? true;
                    const canShare = access?.ownedByUser ?? false;
                    const canOpenDashboard = access?.ownedByUser ?? false;
                    const canCreateVariant = access?.ownedByUser ?? false;

                    return (
                        <div className="flex gap-2">
                            <button
                                className="px-2 py-1 text-xs text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void navigate(`/user/tradingStrategy/${strategy.id}`);
                                }}
                            >
                                {canEdit ? 'Edit' : 'Open'}
                            </button>
                            {canOpenDashboard && (
                                <button
                                    className="px-2 py-1 text-xs text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        void navigate(`/user/strategyDashboard/${strategy.id}`);
                                    }}
                                >
                                    Dashboard
                                </button>
                            )}
                            {canOpenDashboard && (
                                <button
                                    className="px-2 py-1 text-xs text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        void navigate(`/user/aero/new?strategyId=${strategy.id}`);
                                    }}
                                >
                                    AERO
                                </button>
                            )}
                            {canCreateVariant && (
                                <button
                                    className="px-2 py-1 text-xs text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openCreateVariantDialog(strategy);
                                    }}
                                >
                                    Create variation
                                </button>
                            )}
                            {canShare && (
                                <button
                                    className="px-2 py-1 text-xs text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 rounded transition-colors"
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        setSharingStrategy({
                                            id: strategy.id,
                                            label: strategy.name
                                        });
                                    }}
                                >
                                    Share
                                </button>
                            )}
                        </div>
                    );
                }
            }
        ],
        [
            accessByStrategyId,
            baseIdByVariationId,
            coverageByStrategyId,
            coverageSummaryQuery.isLoading,
            isSearching,
            metaByStrategyId,
            openCreateVariantDialog,
            readinessByStrategyId,
            readinessSummaryQuery.isLoading,
            navigate,
            strategyById,
            variationsByBaseId
        ]
    );

    // Expanded panel under a base strategy: its variations, with family-level shortcuts.
    const renderVariations = ({ row }: { row: Row<TradingStrategy> }) => {
        const baseStrategy = row.original;
        const variations = variationsByBaseId.get(baseStrategy.id) ?? [];
        const familyId = familyIdByBaseId.get(baseStrategy.id) ?? null;

        return (
            <div className="px-6 py-3">
                <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                    <span className="text-zinc-500">
                        {variations.length} variation{variations.length === 1 ? '' : 's'} of {baseStrategy.name}
                    </span>
                    {familyId !== null && (
                        <>
                            <Link className="text-blue-400 hover:text-blue-300" to={`/user/strategyFamilies/${familyId}`}>
                                Open family
                            </Link>
                            <Link
                                className="text-blue-400 hover:text-blue-300"
                                to={`/user/strategyVariantComparisons?familyId=${familyId}`}
                            >
                                Compare variations
                            </Link>
                        </>
                    )}
                </div>
                <div className={`${VARIATION_GRID} px-3 pb-1 text-[10px] font-medium uppercase tracking-wide text-zinc-500`}>
                    <span>ID</span>
                    <span>Variation</span>
                    <span>Status</span>
                    <span>Backtests &amp; results</span>
                    <span />
                </div>
                <div className="space-y-1">
                    {variations.map((variation) => {
                        const meta = metaByStrategyId.get(variation.id);
                        const canCreateVariant = accessByStrategyId[variation.id]?.ownedByUser ?? false;
                        const variationSummary = coverageByStrategyId[variation.id];
                        const variationProfitLabel = formatTradeProfitSignal(variationSummary?.avgTradeProfitPercentage);
                        const variationNetLabel = formatNetProfitSignal(variationSummary?.netProfit);
                        const variationWinLabel = formatWinRateSignal(variationSummary?.winRate);

                        const hasVariationResults =
                            !!variationSummary &&
                            variationSummary.tradeCount > 0 &&
                            (!!variationProfitLabel || !!variationNetLabel || !!variationWinLabel);

                        return (
                            <div className={`${VARIATION_GRID} rounded border border-zinc-800 bg-zinc-950/40 px-3 py-2`} key={variation.id}>
                                <div>
                                    <button
                                        className="font-mono text-xs text-blue-400 hover:text-blue-300 hover:underline"
                                        type="button"
                                        onClick={() => navigate(`/user/tradingStrategy/${variation.id}`)}
                                    >
                                        #{variation.id}
                                    </button>
                                </div>
                                <div className="min-w-0">
                                    <button
                                        className="block max-w-full break-words text-left font-medium text-blue-400 hover:text-blue-300 hover:underline"
                                        type="button"
                                        onClick={() => navigate(`/user/tradingStrategy/${variation.id}`)}
                                    >
                                        {variation.name}
                                    </button>
                                    {meta && meta.variantLabel !== variation.name && (
                                        <div className="mt-1 truncate text-[11px] text-zinc-500" title={meta.variantLabel}>
                                            {meta.variantLabel}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <div
                                        className={`mt-1 flex items-center gap-1 text-[11px] ${
                                            variation.active ? 'text-green-400' : 'text-zinc-500'
                                        }`}
                                    >
                                        <span className={`h-1.5 w-1.5 rounded-full ${variation.active ? 'bg-green-400' : 'bg-zinc-600'}`} />
                                        {variation.active ? 'Trading' : 'Paused'}
                                    </div>
                                </div>
                                <div className="min-w-0 text-[11px]">
                                    <button
                                        className={`hover:underline ${
                                            variationSummary?.entitiesWithBacktestsCount
                                                ? 'text-blue-400 hover:text-blue-300'
                                                : 'text-zinc-400 hover:text-zinc-200'
                                        }`}
                                        type="button"
                                        onClick={() => navigate(`/user/tradingStrategy/${variation.id}?section=backtests`)}
                                    >
                                        {formatCoverageLabel(variationSummary)}
                                    </button>
                                    {hasVariationResults && (
                                        <div className="mt-1 text-zinc-400">
                                            <div
                                                className="mb-0.5 text-[10px] text-zinc-500"
                                                title="Best sufficiently covered option-strategy and exit-candidate combination for the strategy's current version, ranked by return on risk capital"
                                            >
                                                Best current-version candidate
                                            </div>
                                            <span className={signalToneClass(variationSummary?.avgTradeProfitPercentage)}>
                                                {variationProfitLabel ?? '—'}
                                            </span>
                                            {variationNetLabel ? (
                                                <>
                                                    {' · '}
                                                    <span className={signalToneClass(variationSummary?.netProfit)}>
                                                        {variationNetLabel}
                                                    </span>
                                                </>
                                            ) : null}
                                            {variationWinLabel ? ` · ${variationWinLabel}` : ''}
                                        </div>
                                    )}
                                </div>
                                <div className="flex justify-end">
                                    {canCreateVariant && (
                                        <button
                                            className="rounded bg-zinc-800 px-2 py-1 text-xs text-zinc-300 transition-colors hover:bg-zinc-700 hover:text-white"
                                            type="button"
                                            onClick={() => openCreateVariantDialog(variation)}
                                        >
                                            New variation
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // Render loading state
    if (isLoading) {
        return (
            <UserLayout title="Trading Strategies - Project 314">
                <DataLoadingSpinner text="Loading trading strategies..." />
            </UserLayout>
        );
    }

    // Render error state
    if (error) {
        return (
            <UserLayout title="Trading Strategies - Error - Project 314">
                <div className="container mx-auto">
                    <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">{error}</div>
                </div>
            </UserLayout>
        );
    }

    return (
        <UserLayout containerSize="1600px" title="Trading Strategies - Project 314">
            <div className="container mx-auto px-4">
                <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h3 className="text-white text-xl font-semibold mb-4">Trading Strategies</h3>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button icon={<FaTrophy />} size="sm" to="/user/strategyLeaderboard" variant="outline-dark">
                            Leaderboard
                        </Button>
                        <Button icon={<FaColumns />} size="sm" to="/user/strategyVariantComparisons" variant="outline-dark">
                            Variant Comparisons
                        </Button>
                    </div>
                </div>

                <ExpandableDataTable
                    allowOverflow
                    striped
                    columns={columns}
                    data={tableStrategies}
                    getRowCanExpand={isSearching ? () => false : (row) => (variationsByBaseId.get(row.original.id)?.length ?? 0) > 0}
                    getRowId={(strategy) => String(strategy.id)}
                    globalFilter={strategySearch}
                    initialSorting={[{ id: 'id', desc: true }]}
                    pagination={{ enabled: true, pageSize: 20 }}
                    renderSubComponent={isSearching ? undefined : renderVariations}
                    searchHint={
                        exactIdMatch ? (
                            <span className="text-zinc-300">
                                Press Enter to open #{exactIdMatch.id} {exactIdMatch.name}
                            </span>
                        ) : undefined
                    }
                    onGlobalFilterChange={setStrategySearch}
                    onSearchSubmit={handleSearchSubmit}
                />
            </div>

            {sharingStrategy && (
                <ShareResourceModal
                    isOpen={!!sharingStrategy}
                    resourceLabel={sharingStrategy.label}
                    resourcePk={sharingStrategy.id}
                    resourceType="tradingStrategy"
                    onClose={() => setSharingStrategy(null)}
                />
            )}

            {variantDialog && (
                <Modal
                    footer={
                        <>
                            <Button disabled={isVariantMutationPending} variant="secondary" onClick={() => setVariantDialog(null)}>
                                Cancel
                            </Button>
                            <Button
                                disabled={
                                    !variantDialog.name.trim() ||
                                    !variantDialog.variantLabel.trim() ||
                                    (variantDialog.scorecardAttachmentMode === 'attach' && !selectedVariantScorecardId) ||
                                    (variantDialog.screenerArtifactMode !== 'none' && sourceScreenerRuleId === null)
                                }
                                isLoading={isVariantMutationPending}
                                onClick={submitVariantDialog}
                            >
                                Create variation
                            </Button>
                        </>
                    }
                    isOpen={!!variantDialog}
                    size="lg"
                    title="Create a variation"
                    onClose={() => setVariantDialog(null)}
                >
                    <div className="grid grid-cols-1 gap-4">
                        <FormInput disabled label="Based on" value={variantDialog.sourceStrategy.name} />
                        <FormInput
                            helpText="How this variation appears in the strategy list and the family view."
                            label="Variation name"
                            maxLength={125}
                            value={variantDialog.name}
                            onChange={(event) =>
                                setVariantDialog((current) =>
                                    current ? { ...current, name: event.target.value, variantLabel: event.target.value } : current
                                )
                            }
                        />
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <FormSelect
                                helpText={
                                    sourceScreenerRuleId !== null
                                        ? 'The source strategy has a screener attached.'
                                        : 'The source strategy has no screener attached.'
                                }
                                label="Screener"
                                options={[
                                    { label: "Don't change the screener", value: 'none' },
                                    {
                                        label: 'Use the same screener (shared)',
                                        value: 'reuseAttached',
                                        disabled: sourceScreenerRuleId === null
                                    },
                                    { label: 'Copy the screener (independent)', value: 'clone', disabled: sourceScreenerRuleId === null },
                                    {
                                        label: 'Use the latest version of the screener',
                                        value: 'latestRevision',
                                        disabled: sourceScreenerRuleId === null
                                    }
                                ]}
                                value={variantDialog.screenerArtifactMode}
                                onChange={(event) => {
                                    const mode = toScreenerArtifactMode(event.target.value);
                                    setVariantDialog((current) => (current ? { ...current, screenerArtifactMode: mode } : current));
                                }}
                            />
                            <FormSelect
                                disabled={variantScorecardsQuery.isLoading}
                                helpText="Locked scorecard versions are reusable. Review and test editable versions before attaching them here."
                                label="Scorecard"
                                options={[
                                    { label: "Don't add a scorecard", value: 'none' },
                                    {
                                        label: 'Attach an existing locked scorecard',
                                        value: 'attach',
                                        disabled: sourceScorecardOptions.length === 0
                                    }
                                ]}
                                value={variantDialog.scorecardAttachmentMode}
                                onChange={(event) => {
                                    const mode = toScorecardAttachmentMode(event.target.value);

                                    setVariantDialog((current) =>
                                        current
                                            ? {
                                                  ...current,
                                                  scorecardAttachmentMode: mode,
                                                  scorecardId:
                                                      mode === 'attach'
                                                          ? (current.scorecardId ??
                                                            chooseDefaultScorecardId(
                                                                sourceScorecards,
                                                                current.sourceStrategy.activeScorecardId ?? null
                                                            ))
                                                          : null
                                              }
                                            : current
                                    );
                                }}
                            />
                            {variantDialog.scorecardAttachmentMode === 'attach' && (
                                <SearchSelect
                                    isDisabled={variantScorecardsQuery.isLoading || sourceScorecardOptions.length === 0}
                                    items={sourceScorecardOptions}
                                    label="Scorecard"
                                    mode="single"
                                    placeholder={sourceScorecardOptions.length > 0 ? 'Choose a scorecard' : 'No locked scorecards'}
                                    value={selectedVariantScorecardId}
                                    onChange={(value) =>
                                        setVariantDialog((current) => (current ? { ...current, scorecardId: value } : current))
                                    }
                                />
                            )}
                        </div>
                        <div className="rounded border border-zinc-700 bg-zinc-900/50 px-3 py-3">
                            <div className="text-sm font-medium text-zinc-200">Global exit candidates</div>
                            <p className="mt-1 text-xs text-zinc-500">
                                The variation inherits the source strategy&apos;s attached global candidates and default candidate by ID. No
                                candidate rows are copied. You can change the attachments after creation.
                            </p>
                        </div>
                        <FormInput
                            label="Description"
                            maxLength={2000}
                            value={variantDialog.description}
                            onChange={(event) =>
                                setVariantDialog((current) => (current ? { ...current, description: event.target.value } : current))
                            }
                        />
                        <VariantPlanSummary state={variantDialog} />
                    </div>
                </Modal>
            )}
        </UserLayout>
    );
};

export default TradingStrategies;
